import { useEffect, useState } from "react";
import axios from "axios";
import { Printer, FileText, FolderCheck, Building, Info, Download } from "lucide-react";
import { SubHeader } from "@/components/SubHeader";
import { Reveal, SectionTag } from "@/components/Reveal";
import { API } from "@/lib/site";

const CHECKLIST = [
  "Health insurance policy copy / e-card",
  "Photo ID proof of the patient (Aadhaar / PAN / voter ID)",
  "Treating doctor's consultation papers and admission advice",
  "Relevant investigation reports (lab, imaging)",
  "Previous medical records related to the condition",
  "Duly filled and signed pre-authorization request form",
  "Hospital estimate with expected line of treatment",
];

const INSURERS = [
  "Star Health & Allied Insurance", "HDFC Ergo General Insurance", "ICICI Lombard", "Niva Bupa Health Insurance",
  "Care Health Insurance", "Tata AIG General Insurance", "Bajaj Allianz General Insurance", "New India Assurance",
  "Oriental Insurance", "United India Insurance", "National Insurance", "SBI General Insurance",
  "Aditya Birla Health Insurance", "ManipalCigna Health Insurance", "Reliance General Insurance", "Go Digit General Insurance",
  "Cholamandalam MS General Insurance", "IFFCO Tokio General Insurance", "Future Generali India Insurance", "Kotak Mahindra General Insurance",
];

const TPAS = [
  "Medi Assist Insurance TPA", "Vidal Health Insurance TPA", "Paramount Health Services & Insurance TPA",
  "Family Health Plan Insurance TPA (FHPL)", "Heritage Health Insurance TPA", "MDIndia Health Insurance TPA",
  "Raksha Health Insurance TPA", "East West Assist Insurance TPA", "Vipul MedCorp Insurance TPA",
  "Health Insurance TPA of India", "Genins India Insurance TPA", "Safeway Insurance TPA",
];

const FORM_ROWS = [
  { section: "Hospital Details" },
  { fields: [["Hospital Name", ""], ["City / Location", ""]] },
  { fields: [["Hospital Insurance Desk Contact", ""]] },
  { section: "Patient Details" },
  { fields: [["Patient Full Name", ""], ["Age / Gender", ""]] },
  { fields: [["UHID / IPD Number", ""], ["Mobile Number", ""]] },
  { section: "Policy Details" },
  { fields: [["Insurance Company / TPA Name", ""], ["Policy Number", ""]] },
  { fields: [["Policy Card / Member ID", ""], ["Policy Period", ""]] },
  { section: "Clinical Details" },
  { fields: [["Treating Doctor", ""], ["Provisional Diagnosis", ""]] },
  { fields: [["Proposed Treatment / Procedure", ""], ["Expected Date of Admission", ""]] },
  { fields: [["Room Category", ""], ["Estimated Treatment Cost (Rs.)", ""]] },
];

export default function Resources() {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    document.title = "Pre-Auth Forms & Resources | Healthcare Solutions";
    return () => {
      document.title = "Healthcare Solutions | Connecting Healthcare. Creating Solutions.";
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/partners`)
      .then(({ data }) => setPartners(data))
      .catch(() => setPartners([]));
  }, []);

  const partnersWithForms = partners.filter((p) => (p.forms || []).length > 0);

  return (
    <div data-testid="resources-page" className="min-h-screen bg-white">
      <SubHeader crumb="Resources" />
      <main className="mx-auto max-w-5xl px-4 pb-24 pt-32 sm:px-6 lg:px-8">
        <Reveal>
          <SectionTag index="RS" label="Forms & Resources" />
          <h1 className="font-display mt-6 max-w-3xl text-3xl font-extrabold tracking-tight text-[#0B3C5C] sm:text-4xl lg:text-5xl">
            Pre-Authorization <span className="text-gradient-teal">Forms & Resources.</span>
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#4A6B82] sm:text-lg">
            A generic cashless pre-authorization request format, a document checklist, and a
            reference directory of insurers and TPAs operating in India.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <section className="mt-14">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-display flex items-center gap-3 text-xl font-bold text-[#0B3C5C] sm:text-2xl">
                <FileText className="h-6 w-6 text-[#4C8F2F]" />
                Generic Cashless Pre-Authorization Request Form
              </h2>
              <button
                onClick={() => window.print()}
                data-testid="preauth-print-button"
                className="inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-6 py-3 text-xs font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F]"
              >
                <Printer className="h-4 w-4" />
                Print / Save as PDF
              </button>
            </div>
            <p className="mt-3 text-sm text-[#4A6B82]">
              This is a generic reference format. Each insurer / TPA may require its own official
              form — always confirm the exact format with the relevant insurance desk or TPA.
            </p>

            <div
              id="preauth-print"
              data-testid="preauth-form"
              className="mt-6 rounded-2xl border border-[#0B3C5C]/15 bg-white p-6 text-slate-900 sm:p-10"
            >
              <div className="border-b-2 border-slate-800 pb-4 text-center">
                <p className="font-display text-lg font-bold tracking-wide sm:text-xl">
                  CASHLESS PRE-AUTHORIZATION REQUEST FORM
                </p>
                <p className="mt-1 text-xs text-[#6B8AA0]">
                  Generic reference format — Healthcare Solutions (healthcare awareness initiative)
                </p>
              </div>
              {FORM_ROWS.map((row, i) =>
                row.section ? (
                  <p key={i} className="mb-2 mt-6 border-b border-slate-300 pb-1 text-xs font-bold uppercase tracking-widest text-slate-700">
                    {row.section}
                  </p>
                ) : (
                  <div key={i} className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
                    {row.fields.map(([label]) => (
                      <div key={label} className="flex items-end gap-2">
                        <span className="whitespace-nowrap text-xs font-semibold text-slate-700">{label}:</span>
                        <span className="mb-0.5 flex-1 border-b border-dotted border-slate-400">&nbsp;</span>
                      </div>
                    ))}
                  </div>
                )
              )}
              <p className="mt-8 text-[11px] leading-relaxed text-[#7E9AAC]">
                Declaration: I hereby authorize the hospital to share my medical and policy-related
                information with the insurer / TPA for the purpose of processing this cashless
                pre-authorization request. I understand that approval is subject to policy terms and
                the final decision of the insurer / TPA.
              </p>
              <div className="mt-10 grid grid-cols-3 gap-6 text-center text-[11px] font-semibold text-slate-700">
                <div className="border-t border-slate-400 pt-2">Patient / Attendant Signature</div>
                <div className="border-t border-slate-400 pt-2">Treating Doctor</div>
                <div className="border-t border-slate-400 pt-2">Hospital Insurance Desk</div>
              </div>
            </div>
          </section>
        </Reveal>

        <Reveal delay={0.12}>
          <section className="mt-16">
            <h2 className="font-display flex items-center gap-3 text-xl font-bold text-[#0B3C5C] sm:text-2xl">
              <FolderCheck className="h-6 w-6 text-amber-500" />
              Cashless Claim Document Checklist
            </h2>
            <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {CHECKLIST.map((item, i) => (
                <li key={item} data-testid={`checklist-item-${i + 1}`}
                  className="flex items-start gap-3 rounded-xl border border-[#0B3C5C]/10 bg-[#EAF3F8]/60 px-4 py-3.5 text-sm text-[#33586F]">
                  <span className="font-mono-x mt-0.5 text-xs font-bold text-[#4C8F2F]">{String(i + 1).padStart(2, "0")}</span>
                  {item}
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        <Reveal delay={0.15}>
          <section className="mt-16">
            <h2 className="font-display flex items-center gap-3 text-xl font-bold text-[#0B3C5C] sm:text-2xl">
              <Building className="h-6 w-6 text-[#4C8F2F]" />
              Insurance Companies & TPA Directory
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-[#4A6B82]">
              A reference list of health insurance companies and Third Party Administrators (TPAs)
              operating in India, to help hospitals and patients identify the relevant contact for
              their policy.
            </p>

            <h3 className="font-mono-x mt-8 text-[11px] font-bold uppercase tracking-[0.3em] text-[#4C8F2F]">
              Health & General Insurers
            </h3>
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="insurer-directory">
              {INSURERS.map((name) => (
                <span key={name} className="rounded-full border border-[#0B3C5C]/15 bg-[#0B3C5C]/[0.04] px-4 py-2 text-xs font-medium text-[#33586F]">
                  {name}
                </span>
              ))}
            </div>

            <h3 className="font-mono-x mt-10 text-[11px] font-bold uppercase tracking-[0.3em] text-amber-500">
              Third Party Administrators (TPAs)
            </h3>
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="tpa-directory">
              {TPAS.map((name) => (
                <span key={name} className="rounded-full border border-[#0B3C5C]/15 bg-[#0B3C5C]/[0.04] px-4 py-2 text-xs font-medium text-[#33586F]">
                  {name}
                </span>
              ))}
            </div>

            <div className="mt-10 flex items-start gap-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-xs leading-relaxed text-[#4A6B82] sm:text-sm">
                This directory is published for general information and navigation only. Listing of
                any insurer or TPA does not indicate any partnership, empanelment, authorization or
                endorsement between Healthcare Solutions and the listed organization. All names and
                trademarks belong to their respective owners. Empanelment, cashless approval and
                claim decisions rest solely with the respective insurer / TPA as per policy terms.
              </p>
            </div>
          </section>
        </Reveal>

        {partnersWithForms.length > 0 && (
          <Reveal delay={0.18}>
            <section className="mt-16" data-testid="company-forms-section">
              <h2 className="font-display flex items-center gap-3 text-xl font-bold text-[#0B3C5C] sm:text-2xl">
                <Download className="h-6 w-6 text-[#4C8F2F]" />
                Company-wise Pre-Authorization Forms
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-[#4A6B82]">
                Download the official pre-authorization form for the insurer or TPA your policy
                belongs to.
              </p>
              <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                {partnersWithForms.map((p) => (
                  <div key={p.id} data-testid={`company-forms-${p.id}`}
                    className="rounded-2xl border border-[#0B3C5C]/10 bg-[#EAF3F8]/60 p-6">
                    <div className="flex items-center gap-4">
                      {p.logo_url ? (
                        <span className="flex h-12 w-24 shrink-0 items-center justify-center rounded-lg bg-white p-1.5">
                          <img src={p.logo_url} alt={p.name} className="max-h-9 max-w-full object-contain" />
                        </span>
                      ) : (
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-[#5FA832]/10">
                          <Building className="h-5 w-5 text-[#4C8F2F]" />
                        </span>
                      )}
                      <div>
                        <p className="font-display text-sm font-bold text-[#0B3C5C]">{p.name}</p>
                        <p className="font-mono-x mt-0.5 text-[10px] uppercase tracking-[0.2em] text-[#4C8F2F]">{p.kind}</p>
                      </div>
                    </div>
                    <ul className="mt-5 space-y-2.5">
                      {p.forms.map((doc, i) => (
                        <li key={i}>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`company-form-download-${p.id}-${i}`}
                            className="group flex items-center justify-between gap-3 rounded-xl border border-[#0B3C5C]/10 bg-white/60 px-4 py-3 text-sm text-[#33586F] transition-all duration-300 hover:border-teal-500/30 hover:text-[#5FA832]"
                          >
                            <span className="flex min-w-0 items-center gap-2.5">
                              <FileText className="h-4 w-4 shrink-0 text-amber-500" />
                              <span className="truncate">{doc.title}</span>
                            </span>
                            <Download className="h-4 w-4 shrink-0 text-[#6B8AA0] transition-colors group-hover:text-[#4C8F2F]" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-[#6B8AA0]">
                These formats belong to the respective insurer / TPA and may change from time to
                time. Always confirm the latest version with the relevant insurance desk.
              </p>
            </section>
          </Reveal>
        )}

        <Reveal delay={0.2}>
          <div className="mt-14 rounded-[2rem] border border-[#0B3C5C]/10 bg-gradient-to-br from-white to-[#EAF3F8] p-9 text-center">
            <h2 className="font-display text-xl font-bold text-[#0B3C5C]">Need help with cashless processes or documentation?</h2>
            <p className="mt-2 text-sm text-[#4A6B82]">Our team supports hospitals with cashless desk processes and claim documentation awareness.</p>
            <a
              href="/#enquiry"
              data-testid="resources-cta-enquiry"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#5FA832] px-8 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-[#4C8F2F]"
            >
              Talk To Our Team
            </a>
          </div>
        </Reveal>
      </main>
    </div>
  );
}
